import { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";

import Flex from "./Flex";

const ColumnContainer = styled.div`
  position: relative;
`;

const ColumnRail = styled.div`
  position: absolute;
  top: 0px;
  right: -10px;
  height: 100%;
  z-index: 1000;
  width: 20px;
  cursor: col-resize;

  ::after {
    display: block;
    position: relative;
    top: 0px;
    left: 8px;
    content: "";
    width: 3px;
    height: 100%;
    background-color: #484848;
    opacity: 0;
    transition: opacity 150ms ease-in-out;
    transition-delay: 150ms;
  }

  :hover,
  :active {
    ::after {
      opacity: 0.3;
    }
  }
`;

const getMousePosition = (event) => {
  return {
    moveX: event.movementX,
    moveY: event.movementY,
    offsetX: event.offsetX,
    offsetY: event.offsetY,
  };
};

const AdjustableColumn = ({
  minWidth,
  maxWidth, // assuming this is the default
  leftPadding,
  minLeftPadding,
  rightPadding,
  minRightPadding,
  children,
  style,
  contain = "strict",
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [rightOffset, setRightOffset] = useState(0);

  const handleMouseMove = useCallback(
    (event) => {
      if (isResizing) {
        const mousePosition = getMousePosition(event);
        setRightOffset((previous) => {
          if (previous + mousePosition.moveX > maxWidth - minWidth) {
            return maxWidth - minWidth;
          }

          if (previous + mousePosition.moveX < minWidth - maxWidth) {
            return minWidth - maxWidth;
          }

          return previous + mousePosition.moveX;
        });
      }
    },
    [isResizing, maxWidth, minWidth]
  );

  const stopResizing = (event) => {
    setIsResizing(false);
  };

  // Start by shrinking the padding
  const _leftPadding = useMemo(() => {
    if (rightOffset >= 0) {
      return leftPadding;
    }

    if ((rightOffset / 2) * -1 > minLeftPadding) {
      return minLeftPadding;
    }

    return leftPadding + rightOffset / 2;
  }, [leftPadding, minLeftPadding, rightOffset]);

  const _rightPadding = useMemo(() => {
    if (rightOffset >= 0) {
      return rightPadding;
    }

    if ((rightOffset / 2) * -1 > minRightPadding) {
      return minRightPadding;
    }

    return rightPadding + rightOffset / 2;
  }, [rightPadding, minRightPadding, rightOffset]);

  const width = useMemo(() => {
    if (rightOffset >= 0) {
      return maxWidth;
    }

    if (maxWidth + rightOffset < minWidth) {
      return minWidth;
    }

    return maxWidth + rightOffset;
  }, [minWidth, maxWidth, rightOffset]);

  useEffect(() => {
    if (isResizing) {
      document.body.addEventListener("mouseleave", stopResizing);
      document.body.addEventListener("mouseup", stopResizing);
      document.body.addEventListener("mousemove", handleMouseMove);
    } else {
      document.body.removeEventListener("mouseleave", stopResizing);
      document.body.removeEventListener("mouseup", stopResizing);
      document.body.removeEventListener("mousemove", handleMouseMove);
    }

    return () => {
      document.body.removeEventListener("mouseleave", stopResizing);
      document.body.removeEventListener("mouseup", stopResizing);
      document.body.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isResizing, handleMouseMove]);

  return (
    <ColumnContainer
      data-resize-container
      style={{
        cursor: isResizing ? "col-resize" : "default",
      }}
    >
      <Flex
        data-resize-container
        direction="column"
        style={{
          ...style,
          contain: contain, // Allowing value flexibility for the 'contain' prop
          width: `${width}px`,
          paddingLeft: `${_leftPadding}px`,
          paddingRight: `${_rightPadding}px`,
        }}
      >
        {children}
      </Flex>
      <ColumnRail
        data-resize-trigger
        onDoubleClick={() => {
          if (Math.abs(rightOffset) > 0) {
            setRightOffset(0);
          }

          if (rightOffset === 0) {
            setRightOffset(
              minWidth - maxWidth + (minLeftPadding - leftPadding) * 2
            );
          }
        }}
        onMouseDown={(event) => {
          setIsResizing(true);
        }}
      />
    </ColumnContainer>
  );
};

export default AdjustableColumn;
